/**
 * Opaque pagination cursor codec (T09).
 *
 * Wire shape (`spec.md` §6.1): `base64url(JSON.stringify({ k, i, c }))`, ≤
 * {@link MAX_CURSOR_BYTES} bytes, opaque to the client. `k` is the sort-key value of
 * the last row on the page, `i` its id — the tuple a keyset query resumes from
 * (`WHERE (k, i) < (?, ?)`), which is why pagination here is never `OFFSET`-based.
 *
 * `c` is a 32-bit FNV-1a checksum of `(k, i)` in hex. It exists so that **every**
 * damaged cursor fails as a clean, typed {@link BadCursorError} — which routes to
 * `400 BAD_CURSOR` (`spec.md` §6.2) — instead of decoding into a half-plausible
 * `{ k, i }` that then explodes inside a SQL binding as a 500. Being explicit,
 * because the distinction matters when someone later goes looking for a MAC:
 * **this is a checksum, not a signature.** It makes tampering *evident*, not
 * *impossible*. A cursor conveys no authority — it is a position in a public,
 * already-authorised listing — so integrity against a motivated forger is not a
 * requirement here; predictable 400s are.
 *
 * Portability and determinism:
 * - No host globals (`Buffer`, `atob`, `TextEncoder`) — this module is bundled into
 *   the web app as well as both Node services.
 * - The JSON payload is escaped to pure ASCII before encoding, so a Cyrillic sort key
 *   needs no UTF-8 encoder and the output is byte-stable across runtimes.
 * - Key order in the payload is fixed (`k`, `i`, `c`), so encoding the same tuple
 *   twice yields the identical string — cursors are safe to compare and to cache.
 */
/** Hard ceiling on the length of an encoded cursor, in bytes (`spec.md` §6.1). */
export const MAX_CURSOR_BYTES = 200;
/**
 * Thrown by {@link decodeCursor} for every rejected input. Carries
 * `code = 'BAD_CURSOR'` so a route handler can map it to `400` without string
 * matching on the message.
 */
export class BadCursorError extends Error {
    code = 'BAD_CURSOR';
    constructor(message) {
        super(message);
        this.name = 'BadCursorError';
    }
}
const B64URL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
const B64URL_REVERSE = (() => {
    const table = new Array(128).fill(-1);
    for (let i = 0; i < B64URL_ALPHABET.length; i += 1) {
        table[B64URL_ALPHABET.charCodeAt(i)] = i;
    }
    return table;
})();
const B64URL_RE = /^[A-Za-z0-9_-]+$/;
/** Unpadded base64url of an ASCII string. */
function b64urlEncodeAscii(text) {
    let out = '';
    const n = text.length;
    for (let i = 0; i < n; i += 3) {
        const a = text.charCodeAt(i);
        const hasB = i + 1 < n;
        const hasC = i + 2 < n;
        const b = hasB ? text.charCodeAt(i + 1) : 0;
        const c = hasC ? text.charCodeAt(i + 2) : 0;
        const triple = (a << 16) | (b << 8) | c;
        out += B64URL_ALPHABET.charAt((triple >>> 18) & 63);
        out += B64URL_ALPHABET.charAt((triple >>> 12) & 63);
        if (hasB)
            out += B64URL_ALPHABET.charAt((triple >>> 6) & 63);
        if (hasC)
            out += B64URL_ALPHABET.charAt(triple & 63);
    }
    return out;
}
/**
 * Unpadded base64url to bytes. Rejects illegal characters and the impossible
 * `length % 4 === 1`. Non-canonical trailing bits are caught by the re-encode
 * comparison in {@link decodeCursor} rather than here, which also covers the
 * one-character mutation that would otherwise be a silent no-op.
 */
function b64urlDecode(text) {
    if (!B64URL_RE.test(text)) {
        throw new BadCursorError('cursor: not base64url');
    }
    if (text.length % 4 === 1) {
        throw new BadCursorError('cursor: truncated base64url');
    }
    const out = [];
    for (let i = 0; i < text.length; i += 4) {
        const remaining = Math.min(4, text.length - i);
        const v = [0, 0, 0, 0];
        for (let j = 0; j < remaining; j += 1) {
            const ch = text.charCodeAt(i + j);
            const value = ch < 128 ? (B64URL_REVERSE[ch] ?? -1) : -1;
            if (value < 0)
                throw new BadCursorError('cursor: not base64url');
            v[j] = value;
        }
        const triple = ((v[0] ?? 0) << 18) | ((v[1] ?? 0) << 12) | ((v[2] ?? 0) << 6) | (v[3] ?? 0);
        out.push((triple >>> 16) & 255);
        if (remaining > 2)
            out.push((triple >>> 8) & 255);
        if (remaining > 3)
            out.push(triple & 255);
    }
    return out;
}
/** Escape every code unit outside printable ASCII as a JSON-style `u` escape. */
function toAscii(text) {
    let out = '';
    for (let i = 0; i < text.length; i += 1) {
        const code = text.charCodeAt(i);
        if (code >= 0x20 && code <= 0x7e) {
            out += text.charAt(i);
        }
        else {
            out += '\\u' + code.toString(16).padStart(4, '0');
        }
    }
    return out;
}
/** FNV-1a/32 over UTF-16 code units. Deterministic on every engine. */
function checksum32(text) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < text.length; i += 1) {
        hash ^= text.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
}
/**
 * The checksummed representation of the tuple. Types are part of it, so a cursor
 * whose `k` flipped from the number `5` to the string `"5"` fails the check instead
 * of quietly changing the meaning of a comparison in SQL.
 */
function checksumOf(k, i) {
    return checksum32(`${typeof k}${String(k)}${typeof i}${String(i)}`);
}
function isCursorScalar(value) {
    if (typeof value === 'string')
        return true;
    return typeof value === 'number' && Number.isFinite(value);
}
/**
 * The one and only spelling of a given tuple: fixed key order, ASCII-escaped, unpadded
 * base64url. Both directions go through this, which is what makes the codec canonical
 * (see {@link decodeCursor}).
 */
function buildToken(k, i) {
    return b64urlEncodeAscii(toAscii(JSON.stringify({ k, i, c: checksumOf(k, i) })));
}
/**
 * Encode a keyset position into an opaque, ≤{@link MAX_CURSOR_BYTES}-byte token.
 *
 * @throws TypeError  when `k`/`i` are absent or are not a string / finite number
 *                    (`NaN`, `Infinity`, objects and `null` are all rejected —
 *                    `JSON.stringify` would turn them into `null` and produce a
 *                    cursor that decodes but means nothing).
 * @throws RangeError when the encoded token would exceed `MAX_CURSOR_BYTES`. That is
 *                    our bug, not the client's: it means an oversized sort key
 *                    (a whole title, say) is being used where a bounded one belongs.
 */
export function encodeCursor(cursor) {
    if (typeof cursor !== 'object' || cursor === null) {
        throw new TypeError('encodeCursor: expected an object { k, i }');
    }
    const { k, i } = cursor;
    if (!isCursorScalar(k)) {
        throw new TypeError(`encodeCursor: k must be a string or a finite number, got ${String(k)}`);
    }
    if (!isCursorScalar(i)) {
        throw new TypeError(`encodeCursor: i must be a string or a finite number, got ${String(i)}`);
    }
    const token = buildToken(k, i);
    if (token.length > MAX_CURSOR_BYTES) {
        throw new RangeError(`encodeCursor: cursor is ${token.length} bytes, over the ${MAX_CURSOR_BYTES}-byte limit`);
    }
    return token;
}
/**
 * Decode a cursor produced by {@link encodeCursor}.
 *
 * Every rejection path throws {@link BadCursorError} — never a raw `SyntaxError`
 * from `JSON.parse`, never a `TypeError` from property access on a non-object.
 * Rejected: non-strings, the empty string, anything over the byte limit, non-
 * base64url text, non-canonical base64url (the mutated-last-character no-op),
 * malformed or truncated JSON, JSON that is not a plain object, unknown or missing
 * keys, wrong value types, and a checksum that does not match.
 */
export function decodeCursor(token) {
    if (typeof token !== 'string') {
        throw new BadCursorError('cursor: expected a string');
    }
    if (token.length === 0) {
        throw new BadCursorError('cursor: empty');
    }
    if (token.length > MAX_CURSOR_BYTES) {
        throw new BadCursorError(`cursor: ${token.length} bytes, over the ${MAX_CURSOR_BYTES}-byte limit`);
    }
    const bytes = b64urlDecode(token);
    let payload = '';
    for (const byte of bytes) {
        if (byte < 0x20 || byte > 0x7e) {
            throw new BadCursorError('cursor: payload is not printable ASCII');
        }
        payload += String.fromCharCode(byte);
    }
    let parsed;
    try {
        parsed = JSON.parse(payload);
    }
    catch {
        throw new BadCursorError('cursor: payload is not JSON');
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        throw new BadCursorError('cursor: payload is not an object');
    }
    const keys = Object.keys(parsed).sort();
    if (keys.length !== 3 || keys[0] !== 'c' || keys[1] !== 'i' || keys[2] !== 'k') {
        throw new BadCursorError(`cursor: unexpected fields [${keys.join(',')}]`);
    }
    const record = parsed;
    const { k, i, c } = record;
    if (!isCursorScalar(k))
        throw new BadCursorError('cursor: k is not a string or finite number');
    if (!isCursorScalar(i))
        throw new BadCursorError('cursor: i is not a string or finite number');
    if (typeof c !== 'string' || c !== checksumOf(k, i)) {
        throw new BadCursorError('cursor: checksum mismatch');
    }
    // Canonicality, and the reason this codec is genuinely tamper-EVIDENT rather than
    // merely checksummed: a tuple has exactly ONE valid token, so anything that is not
    // that token is rejected even when it parses and even when the checksum still
    // matches. Three real holes this closes, all of which a checksum alone leaves open:
    //   - unused low bits in the final base64url character (a mutation there decodes to
    //     the same bytes under a lenient decoder, i.e. tampering with no trace);
    //   - hex case inside an escape, `\u043d` vs `\u043D` — same string, different bytes;
    //   - equivalent number spellings, `0.001` vs `0.0010`, and reordered fields.
    // Re-encoding cannot exceed the byte limit here (the input already passed it and the
    // canonical form is minimal), but it is guarded anyway so no RangeError can escape
    // this function: every rejection must be a BadCursorError.
    let canonical;
    try {
        canonical = buildToken(k, i);
    }
    catch {
        throw new BadCursorError('cursor: not re-encodable');
    }
    if (canonical !== token) {
        throw new BadCursorError('cursor: non-canonical encoding');
    }
    return { k, i };
}
/**
 * Convenience for route handlers: `undefined`/`null`/`''` mean "first page", anything
 * else must decode. Keeps the `if (query.cursor)` dance out of every listing route
 * while preserving the typed failure.
 */
export function decodeCursorOrNull(token) {
    if (token === undefined || token === null || token === '')
        return null;
    return decodeCursor(token);
}
//# sourceMappingURL=cursor.js.map